import type { ViewName, ZonePacking } from "../types";

export type SkylineNode = { x: number; y: number; width: number };
export const createSkyline = (zoneWidth: number): SkylineNode[] => [{ x: 0, y: 0, width: zoneWidth }];

export const findSkylinePosition = (
    skyline: SkylineNode[],
    rectWidth: number,
    rectHeight: number,
    zoneWidth: number,
    zoneHeight: number
) => {
    let bestY = Infinity;
    let bestX = 0;
    let bestIndex = -1;

    for (let nodeIndex = 0; nodeIndex < skyline.length; nodeIndex++) {
        const node = skyline[nodeIndex];
        if (node.width < 1) continue;
        if (node.x + rectWidth > zoneWidth) continue;

        let widthLeft = rectWidth;
        let cursorIndex = nodeIndex;
        let candidateY = 0;
        let cursorX = node.x;

        while (widthLeft > 0 && cursorIndex < skyline.length) {
            if (skyline[cursorIndex].x > cursorX) break;
            candidateY = Math.max(candidateY, skyline[cursorIndex].y);
            const span = Math.min(skyline[cursorIndex].x + skyline[cursorIndex].width - cursorX, widthLeft);
            widthLeft -= span;
            cursorX += span;
            if (widthLeft > 0) cursorIndex++;
        }

        if (widthLeft > 0) continue;
        if (candidateY + rectHeight > zoneHeight) continue;

        const isBetter = candidateY < bestY || (candidateY === bestY && node.x < bestX);
        if (isBetter) {
            bestY = candidateY;
            bestX = node.x;
            bestIndex = nodeIndex;
        }
    }

    return bestIndex >= 0 ? { index: bestIndex, x: bestX, y: bestY } : null;
};

const splitSkylineNode = (skyline: SkylineNode[], index: number, splitX: number) => {
    const node = skyline[index];
    if (splitX <= node.x || splitX >= node.x + node.width) return index;
    const left = { x: node.x, y: node.y, width: splitX - node.x };
    const right = { x: splitX, y: node.y, width: node.x + node.width - splitX };
    skyline.splice(index, 1, left, right);
    return index + 1;
};

const insertIntoSkyline = (
    skyline: SkylineNode[],
    startIndex: number,
    x: number,
    y: number,
    rectWidth: number,
    rectHeight: number
) => {
    let cursorIndex = startIndex;
    cursorIndex = splitSkylineNode(skyline, cursorIndex, x);

    let widthLeft = rectWidth;
    let cursorX = x;

    while (widthLeft > 0) {
        cursorIndex = splitSkylineNode(skyline, cursorIndex, cursorX);
        const node = skyline[cursorIndex];
        const take = Math.min(widthLeft, node.width);

        skyline.splice(
            cursorIndex,
            1,
            { x: cursorX, y: y + rectHeight, width: take },
            { x: cursorX + take, y: node.y, width: node.width - take }
        );

        if (skyline[cursorIndex + 1].width === 0) skyline.splice(cursorIndex + 1, 1);

        widthLeft -= take;
        cursorX += take;
        cursorIndex++;
    }

    for (let mergeIndex = 0; mergeIndex < skyline.length - 1; ) {
        if (skyline[mergeIndex].y === skyline[mergeIndex + 1].y) {
            skyline[mergeIndex].width += skyline[mergeIndex + 1].width;
            skyline.splice(mergeIndex + 1, 1);
        } else {
            mergeIndex++;
        }
    }

    for (let pruneIndex = 0; pruneIndex < skyline.length - 1; ) {
        const a = skyline[pruneIndex];
        const b = skyline[pruneIndex + 1];
        if (a.y <= b.y && a.x + a.width >= b.x + b.width) {
            skyline.splice(pruneIndex + 1, 1);
        } else {
            pruneIndex++;
        }
    }
};

export const packZoneWithSkyline = (
    zoneWidth: number,
    zoneHeight: number,
    rectangles: Array<{ name: ViewName; w: number; h: number }>
): ZonePacking => {
    const skyline = createSkyline(zoneWidth);
    const placed: any[] = [];
    const failed: ViewName[] = [];
    const orderBySize = [...rectangles].sort((a, b) => b.h - a.h || b.w - a.w);

    for (const rectangleItem of orderBySize) {
        const position = findSkylinePosition(skyline, rectangleItem.w, rectangleItem.h, zoneWidth, zoneHeight);
        if (!position) {
            failed.push(rectangleItem.name);
            continue;
        }
        placed.push({ name: rectangleItem.name, x: position.x, y: position.y, w: rectangleItem.w, h: rectangleItem.h });
        insertIntoSkyline(skyline, position.index, position.x, position.y, rectangleItem.w, rectangleItem.h);
    }

    const usedHeight = skyline.reduce((maxHeight, node) => Math.max(maxHeight, node.y), 0);
    return { placed, failed, usedHeight } as ZonePacking;
};
